import { registerLocaleVariant } from "../../registry";
import { makeId } from "../../helpers";
import type { ProgramBody } from "../../types";

// ─── Helper ─────────────────────────────────────────────────────────

function frProgram(
  slug: string,
  variant: {
    title: string;
    subtitle: string;
    body: Partial<ProgramBody>;
  },
): void {
  registerLocaleVariant(makeId(slug), "fr", variant);
}

// ─── Assessment ─────────────────────────────────────────────────────

frProgram("program-fertility-check-in", {
  title: "Bilan de fertilité",
  subtitle:
    "Connais tes chiffres — pour comprendre où vous en êtes tous les deux",
  body: {
    duration: "2 semaines",
    includes: [
      "Panel hormonal complet pour elle (AMH, FSH, LH, œstradiol, thyroïde)",
      "Échographie endovaginale avec comptage des follicules antraux",
      "Spermogramme pour lui",
      "Revue des résultats avec une infirmière de fertilité",
      "Rapport personnalisé sur les prochaines étapes",
    ],
    phases: [
      {
        title: "Bilan",
        description: "Les deux partenaires testés en parallèle",
        moduleIds: [
          makeId("module-female-hormone-baseline"),
          makeId("module-male-fertility-baseline"),
        ],
        parallel: true,
      },
    ],
    steps: [
      {
        title: "Prise de rendez-vous",
        description:
          "Réserve les analyses et l'imagerie. Sa prise de sang doit avoir lieu entre J2 et J3 du cycle.",
      },
      {
        title: "Faire les examens",
        description:
          "Panel hormonal et échographie pour elle, spermogramme pour lui.",
      },
      {
        title: "Revue des résultats",
        description:
          "Échange de 30 minutes pour parcourir tous les chiffres et discuter des options.",
      },
    ],
  },
});

// ─── Optimization ───────────────────────────────────────────────────

frProgram("program-her-optimization", {
  title: "Optimisation 90 jours — Elle",
  subtitle:
    "Optimise l'environnement de tes ovocytes en maturation avant le traitement",
  body: {
    duration: "3 mois",
    includes: [
      "Bilan de fertilité initial (AMH, FSH, LH, œstradiol, thyroïde, vitamine D)",
      "Protocole ciblé de compléments (CoQ10, vitamine D, oméga-3, méthylfolate)",
      "Accompagnement mode de vie (sommeil, exercice, gestion du stress)",
      "Questionnaires de suivi mensuels",
      "Bilan de contrôle à 90 jours pour suivre les tendances",
    ],
    phases: [
      {
        title: "Bilan",
        description: "Comprendre tes chiffres de départ",
        moduleIds: [makeId("module-female-hormone-baseline")],
      },
      {
        title: "Optimiser",
        description: "90 jours de compléments et de changements de mode de vie",
        moduleIds: [makeId("module-female-optimization")],
      },
    ],
    steps: [
      {
        title: "Bilan",
        description:
          "Panel hormonal et échographie pour établir ta situation de départ.",
      },
      {
        title: "Démarrer le protocole",
        description:
          "Commence les compléments et les ajustements de mode de vie pendant la fenêtre de 90 jours de maturation ovocytaire.",
      },
      {
        title: "Contrôle",
        description:
          "Refais les analyses à 90 jours. Vitamine D et thyroïde devraient être optimisées. L'AMH est suivie en tendance.",
      },
    ],
  },
});

frProgram("program-his-optimization", {
  title: "Optimisation 90 jours — Lui",
  subtitle:
    "Améliore concrètement la santé spermatique — des chiffres réels, des progrès mesurables",
  body: {
    duration: "3 mois",
    includes: [
      "Spermogramme initial avec morphologie détaillée",
      "Stack ciblé de compléments (CoQ10, zinc, vitamine D, oméga-3)",
      "Protocole mode de vie (éviter la chaleur, exercice, sommeil, alcool)",
      "Points mensuels avec suivi des progrès",
      "Spermogramme de contrôle à 90 jours",
      "En option : test de fragmentation de l'ADN",
    ],
    phases: [
      {
        title: "Bilan",
        description: "Spermogramme pour connaître tes chiffres de départ",
        moduleIds: [makeId("module-male-fertility-baseline")],
      },
      {
        title: "Optimiser",
        description: "90 jours ciblant l'ensemble du cycle de spermatogenèse",
        moduleIds: [makeId("module-male-optimization")],
      },
    ],
    steps: [
      {
        title: "Spermogramme initial",
        description:
          "Spermogramme pour mesurer numération, mobilité et morphologie.",
      },
      {
        title: "Démarrer le protocole",
        description:
          "Compléments et changements de mode de vie ciblant le cycle de spermatogenèse de 74 jours.",
      },
      {
        title: "Contrôle à 90 jours",
        description:
          "Refais le spermogramme pour mesurer l'amélioration de la numération, de la mobilité et de la morphologie.",
      },
    ],
  },
});

frProgram("program-couples-optimization", {
  title: "Optimisation en couple",
  subtitle: "Les deux partenaires optimisent ensemble pendant 90 jours",
  body: {
    duration: "3 mois",
    includes: [
      "Bilan de fertilité pour elle (panel hormonal, échographie)",
      "Spermogramme pour lui",
      "Protocoles de compléments ciblés pour les deux partenaires",
      "Accompagnement mode de vie pour les deux",
      "Points mensuels pour chacun",
      "Bilans de contrôle à 90 jours pour les deux (analyses pour elle, spermogramme pour lui)",
    ],
    phases: [
      {
        title: "Bilan",
        description: "Les deux partenaires évalués en parallèle",
        moduleIds: [
          makeId("module-female-hormone-baseline"),
          makeId("module-male-fertility-baseline"),
        ],
        parallel: true,
      },
      {
        title: "Optimiser ensemble",
        description:
          "90 jours de compléments et de changements de mode de vie pour les deux",
        moduleIds: [
          makeId("module-female-optimization"),
          makeId("module-male-optimization"),
        ],
        parallel: true,
      },
    ],
    steps: [
      {
        title: "Bilan pour les deux",
        description:
          "Panel hormonal et échographie pour elle, spermogramme pour lui.",
      },
      {
        title: "Protocoles en parallèle",
        description:
          "Les deux démarrent simultanément des protocoles personnalisés de compléments et de mode de vie.",
      },
      {
        title: "Contrôle ensemble",
        description:
          "Les deux refont leurs examens à 90 jours pour mesurer les progrès avant toute décision de traitement.",
      },
    ],
  },
});

// ─── Treatment ──────────────────────────────────────────────────────

frProgram("program-egg-freezing", {
  title: "Autoconservation d'ovocytes",
  subtitle:
    "Préserve tes ovocytes — du bilan à la ponction et à la conservation",
  body: {
    duration: "2 semaines à 4 mois",
    includes: [
      "Bilan de fertilité initial (AMH, CFA, panel hormonal)",
      "Optimisation de la qualité ovocytaire sur 90 jours en option",
      "Mise en relation avec un centre selon ta localisation et tes priorités",
      "Stimulation ovarienne avec suivi",
      "Ponction ovocytaire",
      "Cryoconservation et conservation annuelle",
      "Comparaison du coût des médicaments et accompagnement au financement",
    ],
    phases: [
      {
        title: "Bilan",
        description: "Comprendre ton état de fertilité de départ",
        moduleIds: [makeId("module-female-hormone-baseline")],
      },
      {
        title: "Optimisation (en option)",
        description: "90 jours de compléments et de changements de mode de vie",
        moduleIds: [makeId("module-female-optimization")],
      },
      {
        title: "Stimulation",
        description: "10 à 14 jours d'injections quotidiennes avec suivi",
        moduleIds: [makeId("module-ovarian-stimulation")],
      },
      {
        title: "Ponction et conservation",
        description: "Ponction ovocytaire suivie de la cryoconservation",
        moduleIds: [makeId("module-egg-retrieval")],
      },
    ],
    steps: [
      {
        title: "Bilan",
        description:
          "Analyses de fertilité et échographie pour comprendre ton point de départ et planifier le cycle.",
      },
      {
        title: "Optimisation (en option)",
        description:
          "8 à 12 semaines de compléments ciblés et de changements de mode de vie avant la stimulation.",
      },
      {
        title: "Stimulation",
        description:
          "10 à 14 jours d'injections quotidiennes avec suivi tous les 2 à 3 jours.",
      },
      {
        title: "Ponction et conservation",
        description:
          "Ponction ovocytaire (environ 20 minutes), cryoconservation et conservation annuelle.",
      },
    ],
  },
});

frProgram("program-embryo-freezing", {
  title: "Congélation d'embryons",
  subtitle: "Créer et congeler des embryons — du bilan au dépistage génétique",
  body: {
    duration: "3 semaines à 5 mois",
    includes: [
      "Bilan de fertilité complet pour les deux partenaires",
      "Optimisation de 90 jours en option pour elle et pour lui",
      "Stimulation ovarienne avec suivi",
      "Ponction ovocytaire",
      "Fécondation et culture embryonnaire jusqu'au blastocyste",
      "Dépistage génétique PGT-A des embryons en option",
      "Cryoconservation et conservation annuelle",
    ],
    phases: [
      {
        title: "Bilan",
        description: "Comprendre où vous en êtes tous les deux",
        moduleIds: [
          makeId("module-female-hormone-baseline"),
          makeId("module-male-fertility-baseline"),
        ],
        parallel: true,
      },
      {
        title: "Optimisation (en option)",
        description: "90 jours pour préparer les deux partenaires",
        moduleIds: [
          makeId("module-female-optimization"),
          makeId("module-male-optimization"),
        ],
        parallel: true,
      },
      {
        title: "Stimulation et ponction",
        description: "Stimulation, ponction et recueil de sperme",
        moduleIds: [
          makeId("module-ovarian-stimulation"),
          makeId("module-egg-retrieval"),
        ],
      },
      {
        title: "Fécondation et tests",
        description:
          "Développement embryonnaire et dépistage génétique en option",
        moduleIds: [
          makeId("module-fertilization-culture"),
          makeId("module-pgt-a"),
        ],
      },
    ],
    steps: [
      {
        title: "Bilan pour les deux",
        description:
          "Panel hormonal et échographie pour elle, spermogramme pour lui.",
      },
      {
        title: "Optimisation (en option)",
        description:
          "90 jours de compléments et de changements de mode de vie pour les deux partenaires.",
      },
      {
        title: "Stimulation et ponction",
        description:
          "10 à 14 jours de stimulation, puis ponction ovocytaire et recueil de sperme.",
      },
      {
        title: "Fécondation et tests",
        description:
          "Fécondation, culture jusqu'au blastocyste, dépistage PGT-A en option, puis congélation.",
      },
    ],
  },
});

frProgram("program-ivf-cycle", {
  title: "Cycle de FIV",
  subtitle: "FIV de bout en bout — du bilan au transfert",
  body: {
    duration: "4 semaines à 6 mois",
    includes: [
      "Bilan de fertilité complet pour les deux partenaires",
      "Optimisation de 90 jours en option pour elle et pour lui",
      "Stimulation ovarienne avec suivi quotidien",
      "Ponction ovocytaire et recueil de sperme",
      "Fécondation et culture embryonnaire jusqu'au blastocyste",
      "Dépistage génétique PGT-A en option",
      "Transfert d'embryon avec soutien de phase lutéale",
      "Accompagnement pendant les 2 semaines d'attente et dosage de bêta-hCG",
    ],
    phases: [
      {
        title: "Bilan",
        description: "Bilan complet pour les deux partenaires",
        moduleIds: [
          makeId("module-female-hormone-baseline"),
          makeId("module-male-fertility-baseline"),
        ],
        parallel: true,
      },
      {
        title: "Optimisation (en option)",
        description: "90 jours de préparation pour les deux",
        moduleIds: [
          makeId("module-female-optimization"),
          makeId("module-male-optimization"),
        ],
        parallel: true,
      },
      {
        title: "Stimulation et ponction",
        description: "Stimulation, ponction, fécondation",
        moduleIds: [
          makeId("module-ovarian-stimulation"),
          makeId("module-egg-retrieval"),
          makeId("module-fertilization-culture"),
        ],
      },
      {
        title: "Dépistage (en option)",
        description: "Test génétique PGT-A des embryons",
        moduleIds: [makeId("module-pgt-a")],
      },
      {
        title: "Transfert et attente",
        description: "Transfert d'embryon et 2 semaines d'attente",
        moduleIds: [
          makeId("module-embryo-transfer"),
          makeId("module-tww-support"),
        ],
      },
    ],
    steps: [
      {
        title: "Bilan",
        description: "Bilan de fertilité complet pour les deux partenaires.",
      },
      {
        title: "Optimisation (en option)",
        description:
          "90 jours de compléments et de changements de mode de vie.",
      },
      {
        title: "Stimulation et ponction",
        description:
          "10 à 14 jours de stimulation, ponction, fécondation et culture embryonnaire.",
      },
      {
        title: "Transfert",
        description:
          "Transfert d'un seul embryon avec soutien progestatif et accompagnement pendant les 2 semaines d'attente.",
      },
    ],
  },
});

frProgram("program-iui-cycle", {
  title: "Cycle d'IIU",
  subtitle:
    "IIU médicamenteuse ou naturelle avec suivi et accompagnement de l'attente",
  body: {
    duration: "1 à 2 mois",
    includes: [
      "Bilan de fertilité pour elle",
      "Suivi de l'ovulation par échographie",
      "Déclenchement programmé (en cycle médicamenteux)",
      "Préparation du sperme et insémination intra-utérine",
      "Accompagnement pendant les 2 semaines d'attente et dosage de bêta-hCG",
    ],
    phases: [
      {
        title: "Bilan",
        description: "Confirmer la fonction ovarienne et le timing",
        moduleIds: [makeId("module-female-hormone-baseline")],
      },
      {
        title: "Insémination",
        description: "Suivi, déclenchement et procédure d'IIU",
        moduleIds: [makeId("module-iui-procedure")],
      },
      {
        title: "2 semaines d'attente",
        description: "Accompagnement pendant la période d'attente",
        moduleIds: [makeId("module-tww-support")],
      },
    ],
    steps: [
      {
        title: "Bilan et suivi",
        description:
          "Confirme le timing de l'ovulation par échographie. Démarre les médicaments si prescrits.",
      },
      {
        title: "Procédure d'IIU",
        description:
          "Préparation du sperme et insémination. Procédure rapide, au cabinet.",
      },
      {
        title: "Attente et test",
        description:
          "2 semaines d'attente avec accompagnement. Dosage de bêta-hCG à 14 jours.",
      },
    ],
  },
});

frProgram("program-fet-cycle", {
  title: "Transfert d'embryon congelé (TEC)",
  subtitle:
    "Transfère un embryon précédemment congelé avec un plan de soins établi par le centre",
  body: {
    duration: "La durée varie selon le protocole et le centre",
    includes: [
      "Préparation endométriale choisie par le centre",
      "Suivi et calendrier consignés par l'équipe soignante",
      "Procédure de transfert d'embryon",
      "Résultats consignés par l'embryologie et pour le transfert",
      "Plan de test et de suivi communiqué par l'équipe soignante",
    ],
    phases: [
      {
        title: "Préparation et transfert",
        description: "Préparation et transfert selon les consignes du centre",
        moduleIds: [makeId("module-embryo-transfer")],
      },
      {
        title: "2 semaines d'attente",
        description: "Accompagnement en attendant le suivi prescrit",
        moduleIds: [makeId("module-tww-support")],
      },
    ],
    steps: [
      {
        title: "Préparation endométriale",
        description:
          "Le centre choisit la préparation, le suivi et le calendrier de ce transfert.",
      },
      {
        title: "Transfert",
        description:
          "Le centre consigne la décongélation et indique si le transfert prévu a eu lieu, y compris en cas d'annulation ou d'absence de transfert.",
      },
      {
        title: "Attente et test",
        description:
          "L'équipe consigne le test prescrit et le suivi prévu ; le calendrier et les consignes de médicaments varient selon le protocole.",
      },
    ],
  },
});

frProgram("program-genetic-screening", {
  title: "Dépistage génétique",
  subtitle:
    "Dépistage des porteurs sains pour identifier les risques de maladies héréditaires",
  body: {
    duration: "4 à 8 semaines",
    includes: [
      "Panel élargi de dépistage des porteurs sains pour les deux partenaires (plus de 200 conditions)",
      "Prélèvement sanguin ou salivaire",
      "Consultation avec un conseiller en génétique",
      "Recommandations concrètes si les deux partenaires portent la même condition",
      "Recommandations PGT-M si applicable",
    ],
    phases: [
      {
        title: "Dépistage",
        description: "Panel de porteurs sains pour les deux partenaires",
        moduleIds: [makeId("module-genetic-carrier")],
      },
    ],
    steps: [
      {
        title: "Prélèvement",
        description:
          "Prise de sang ou kit salivaire pour chaque partenaire. En laboratoire ou à domicile.",
      },
      {
        title: "Analyse en laboratoire",
        description:
          "L'analyse génétique prend 2 à 4 semaines. Dépistage de plus de 200 conditions.",
      },
      {
        title: "Conseil",
        description:
          "Le conseiller en génétique passe en revue les résultats et discute des implications.",
      },
    ],
  },
});

frProgram("program-full-assessment", {
  title: "Bilan de fertilité et génomique complet",
  subtitle:
    "Le bilan de fertilité le plus complet possible — hormones, métabolisme, génétique et génomique dans un seul programme",
  body: {
    duration: "6 à 12 semaines",
    includes: [
      "Bilan de fertilité — AMH, FSH, LH, œstradiol, échographie avec CFA pour elle ; spermogramme pour lui",
      "Panel de santé de plus de 100 biomarqueurs via un laboratoire partenaire — hormones, thyroïde, métabolisme, lipides, inflammation, nutriments, foie, reins, NFS, marqueurs auto-immuns, et plus",
      "Compléments spécifiques à la fertilité non couverts par le panel — anticorps antiphospholipides, génétique de la coagulation (Facteur V Leiden, MTHFR), progestérone en milieu de phase lutéale, sérologies infectieuses",
      "Panel élargi de dépistage des porteurs sains (plus de 200 conditions) pour les deux partenaires",
      "Séquençage de l'exome entier ou du génome entier pour les deux partenaires",
      "Profilage pharmacogénomique — comment tu métabolises les médicaments de fertilité (létrozole, gonadotrophines, progestérone)",
      "Scores de risque polygénique pour le PMOS (ex SOPK), l'endométriose, l'IOP, les fausses couches à répétition",
      "Session approfondie avec un conseiller en génétique pour les résultats génomiques complexes",
      "Synthèse complète — session de 60 minutes reliant chaque résultat en un plan d'action unique",
    ],
    phases: [
      {
        title: "Bilan de fertilité",
        description:
          "Examens de fertilité standard pour les deux partenaires en parallèle",
        moduleIds: [
          makeId("module-female-hormone-baseline"),
          makeId("module-male-fertility-baseline"),
        ],
        parallel: true,
      },
      {
        title: "Panel de santé complet",
        description:
          "Plus de 100 biomarqueurs via le laboratoire partenaire — métabolisme, hormones, inflammation, auto-immunité, nutrition, foie, reins, et plus",
        moduleIds: [makeId("module-comprehensive-wellness")],
      },
      {
        title: "Génétique et génomique",
        description:
          "Dépistage des porteurs sains plus séquençage de l'exome ou du génome avec pharmacogénomique",
        moduleIds: [
          makeId("module-genetic-carrier"),
          makeId("module-comprehensive-genomic"),
        ],
        parallel: true,
      },
    ],
    steps: [
      {
        title: "Bilan de fertilité",
        description:
          "AMH, panel hormonal et échographie pour elle (J2 à J3 du cycle), spermogramme pour lui. Résultats sous 1 à 2 semaines.",
      },
      {
        title: "Panel de santé complet",
        description:
          "Une seule prise de sang matinale à jeun pour plus de 100 biomarqueurs via notre laboratoire partenaire — métabolisme, hormones, inflammation, nutrition, auto-immunité, foie, reins, NFS. Plus des compléments spécifiques à la fertilité.",
      },
      {
        title: "Prélèvements génétiques et génomiques",
        description:
          "Sang ou salive pour le dépistage des porteurs sains (plus de 200 conditions) et le séquençage de l'exome ou du génome pour les deux partenaires.",
      },
      {
        title: "Résultats par étapes",
        description:
          "Bilan de fertilité : 1 à 2 semaines. Panel de santé : 1 à 2 semaines. Dépistage des porteurs sains : 2 à 4 semaines. Profilage génomique : 6 à 12 semaines.",
      },
      {
        title: "Synthèse complète",
        description:
          "Session de 60 minutes reliant chaque résultat — de tes niveaux d'insuline à ton profil pharmacogénomique — en un plan reproductif unique et actionnable.",
      },
    ],
  },
});
